'use client';
import React, { useState, useCallback, useMemo } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SharedLink {
  id: string;
  qrId: string;
  qrType: string;
  qrContent: string;
  slug: string;
  title: string;
  createdAt: string;
  expiresAt: string | null;
  views: number;
  uniqueVisitors: number;
  clicks: number;
  lastClickedAt: string | null;
  active: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
// Backend integration point: GET /api/shareable-links
const MOCK_LINKS: SharedLink[] = [
  {
    id: 'sl-001', qrId: 'qr-006', qrType: 'url',
    qrContent: 'https://novamaxstudios.com/portfolio',
    slug: 'nova-portfolio-qr', title: 'NovaMax Portfolio QR',
    createdAt: '2026-08-01T09:05:00Z', expiresAt: null,
    views: 1248, uniqueVisitors: 834, clicks: 389, lastClickedAt: '2026-08-03T12:45:00Z', active: true,
  },
  {
    id: 'sl-002', qrId: 'qr-009', qrType: 'text',
    qrContent: 'Certificate of Completion — NovaMax Studios',
    slug: 'certificate-qr-2026', title: 'Certificate QR 2026',
    createdAt: '2026-07-30T10:00:00Z', expiresAt: '2026-12-31T23:59:59Z',
    views: 2103, uniqueVisitors: 1567, clicks: 504, lastClickedAt: '2026-08-03T11:30:00Z', active: true,
  },
  {
    id: 'sl-003', qrId: 'qr-003', qrType: 'whatsapp',
    qrContent: 'https://wa.me/919876543210',
    slug: 'novamax-whatsapp', title: 'NovaMax WhatsApp',
    createdAt: '2026-08-02T16:45:00Z', expiresAt: null,
    views: 643, uniqueVisitors: 412, clicks: 211, lastClickedAt: '2026-08-03T10:15:00Z', active: true,
  },
  {
    id: 'sl-004', qrId: 'qr-010', qrType: 'url',
    qrContent: 'https://knowourindia.com/states/rajasthan',
    slug: 'rajasthan-tourism-qr', title: 'Rajasthan Tourism QR',
    createdAt: '2026-07-29T08:30:00Z', expiresAt: '2026-09-01T00:00:00Z',
    views: 521, uniqueVisitors: 310, clicks: 178, lastClickedAt: '2026-08-02T18:00:00Z', active: true,
  },
  {
    id: 'sl-005', qrId: 'qr-001', qrType: 'url',
    qrContent: 'https://knowourindia.com',
    slug: 'know-our-india-main', title: 'Know Our India — Main',
    createdAt: '2026-08-03T10:22:00Z', expiresAt: null,
    views: 389, uniqueVisitors: 241, clicks: 142, lastClickedAt: '2026-08-03T13:00:00Z', active: true,
  },
  {
    id: 'sl-006', qrId: 'qr-005', qrType: 'email',
    qrContent: 'mailto:hello@knowourindia.com',
    slug: 'koi-contact-email', title: 'KOI Contact Email',
    createdAt: '2026-08-01T11:20:00Z', expiresAt: null,
    views: 198, uniqueVisitors: 134, clicks: 76, lastClickedAt: '2026-08-02T09:40:00Z', active: false,
  },
];

const BASE_URL = 'https://qr.novamaxinfotech.com/s/';

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  url:      { label: 'URL',       color: '#2563EB', bg: '#EFF6FF' },
  upi:      { label: 'UPI',       color: '#7C3AED', bg: '#F5F3FF' },
  whatsapp: { label: 'WhatsApp',  color: '#16A34A', bg: '#F0FDF4' },
  wifi:     { label: 'WiFi',      color: '#D97706', bg: '#FFFBEB' },
  email:    { label: 'Email',     color: '#9333EA', bg: '#FAF5FF' },
  maps:     { label: 'Maps',      color: '#DC2626', bg: '#FFF1F2' },
  phone:    { label: 'Phone',     color: '#0891B2', bg: '#ECFEFF' },
  text:     { label: 'Text',      color: '#475569', bg: '#F8FAFC' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon, accent }: {
  label: string; value: string | number; sub?: string; icon: React.ReactNode; accent: string;
}) {
  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-5 flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: accent }}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-muted-foreground font-medium truncate" style={{ fontSize: '12px' }}>{label}</p>
        <p className="font-extrabold text-foreground mt-0.5" style={{ fontSize: '24px', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</p>
        {sub && <p className="text-muted-foreground mt-1" style={{ fontSize: '12px' }}>{sub}</p>}
      </div>
    </div>
  );
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card hover:bg-muted/50 transition-all duration-150 flex-shrink-0"
      style={{ fontSize: '12px', fontWeight: 600, color: copied ? '#16A34A' : 'var(--foreground)' }}
    >
      {copied ? (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="2,7 5,10 11,3" />
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="7.5" height="7.5" rx="1.2" />
          <path d="M1.5 9V2.5A1 1 0 0 1 2.5 1.5H9" />
        </svg>
      )}
      {copied ? 'Copied!' : label}
    </button>
  );
}

function EmbedModal({ link, onClose }: { link: SharedLink; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<'iframe' | 'button' | 'badge'>('iframe');
  const shareUrl = `${BASE_URL}${link.slug}`;

  const snippets = {
    iframe: `<iframe\n  src="${shareUrl}"\n  width="300"\n  height="300"\n  frameborder="0"\n  style="border-radius:16px;border:1px solid #e2e8f0;"\n  title="${link.title}"\n></iframe>`,
    button: `<a\n  href="${shareUrl}"\n  target="_blank"\n  rel="noopener noreferrer"\n  style="display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:linear-gradient(135deg,#2563EB,#7C3AED);color:#fff;border-radius:12px;font-family:sans-serif;font-size:14px;font-weight:600;text-decoration:none;"\n>\n  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" stroke-width="1.8"><rect x="1" y="1" width="5" height="5" rx="1"/><rect x="10" y="1" width="5" height="5" rx="1"/><rect x="1" y="10" width="5" height="5" rx="1"/><rect x="10" y="10" width="2" height="2" rx=".4" fill="white"/><rect x="14" y="10" width="2" height="2" rx=".4" fill="white"/><rect x="10" y="14" width="2" height="2" rx=".4" fill="white"/><rect x="14" y="14" width="2" height="2" rx=".4" fill="white"/></svg>\n  Scan QR Code\n</a>`,
    badge: `<img\n  src="${shareUrl}/badge"\n  alt="${link.title} QR Badge"\n  width="200"\n  style="border-radius:12px;"\n/>`,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-card rounded-3xl border border-border shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Embed HTML Snippet</h3>
            <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>{link.title}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2 rounded-xl" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex gap-2">
          {(['iframe', 'button', 'badge'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-4 py-1.5 rounded-xl font-semibold transition-all duration-150"
              style={{
                fontSize: '12px',
                background: activeTab === tab ? 'var(--primary)' : 'var(--muted)',
                color: activeTab === tab ? 'white' : 'var(--muted-foreground)',
              }}
            >
              {tab === 'iframe' ? 'iFrame' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Code block */}
        <div className="px-6 py-4">
          <div className="relative rounded-2xl overflow-hidden border border-border" style={{ background: '#0F172A' }}>
            <pre className="p-4 overflow-x-auto text-green-400" style={{ fontSize: '12px', lineHeight: 1.6, fontFamily: 'JetBrains Mono, IBM Plex Mono, monospace' }}>
              {snippets[activeTab]}
            </pre>
            <div className="absolute top-3 right-3">
              <CopyButton text={snippets[activeTab]} label="Copy HTML" />
            </div>
          </div>
          <p className="text-muted-foreground mt-3" style={{ fontSize: '11px' }}>
            Paste this snippet into any webpage to embed the QR link. Analytics will be tracked automatically.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-muted-foreground" style={{ fontSize: '12px' }}>Live tracking enabled</span>
          </div>
          <button onClick={onClose} className="btn-primary px-5 py-2 rounded-xl" style={{ fontSize: '13px' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function GenerateLinkModal({ onClose, onCreate }: { onClose: () => void; onCreate: (link: SharedLink) => void }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [qrContent, setQrContent] = useState('');
  const [qrType, setQrType] = useState('url');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const autoSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const finalSlug = slugTouched ? slug : autoSlug;

  const handleCreate = () => {
    if (!title.trim() || !qrContent.trim()) return;
    const newLink: SharedLink = {
      id: `sl-${Date.now()}`,
      qrId: `qr-new-${Date.now()}`,
      qrType,
      qrContent,
      slug: finalSlug || `link-${Date.now()}`,
      title,
      createdAt: new Date().toISOString(),
      expiresAt: hasExpiry && expiryDate ? new Date(expiryDate).toISOString() : null,
      views: 0, uniqueVisitors: 0, clicks: 0, lastClickedAt: null, active: true,
    };
    onCreate(newLink);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-card rounded-3xl border border-border shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-bold text-foreground" style={{ fontSize: '16px' }}>Generate Shareable Link</h3>
            <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Create a unique trackable link for a QR code</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2 rounded-xl" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-foreground font-semibold mb-1.5" style={{ fontSize: '13px' }}>Link Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. NovaMax Portfolio QR"
              className="w-full px-4 py-2.5 rounded-2xl border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* QR Type */}
          <div>
            <label className="block text-foreground font-semibold mb-1.5" style={{ fontSize: '13px' }}>QR Type</label>
            <select
              value={qrType}
              onChange={e => setQrType(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl border border-border bg-muted/30 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              style={{ fontSize: '14px' }}
            >
              {Object.entries(TYPE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* QR Content */}
          <div>
            <label className="block text-foreground font-semibold mb-1.5" style={{ fontSize: '13px' }}>QR Content / URL</label>
            <input
              type="text"
              value={qrContent}
              onChange={e => setQrContent(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-2.5 rounded-2xl border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* Custom Slug */}
          <div>
            <label className="block text-foreground font-semibold mb-1.5" style={{ fontSize: '13px' }}>Custom Slug <span className="text-muted-foreground font-normal">(optional)</span></label>
            <div className="flex items-center rounded-2xl border border-border bg-muted/30 overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-all">
              <span className="px-3 text-muted-foreground border-r border-border bg-muted/50 py-2.5 flex-shrink-0" style={{ fontSize: '12px' }}>/s/</span>
              <input
                type="text"
                value={finalSlug}
                onChange={e => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }}
                placeholder={autoSlug || 'my-qr-link'}
                className="flex-1 px-3 py-2.5 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
                style={{ fontSize: '14px' }}
              />
            </div>
            <p className="text-muted-foreground mt-1" style={{ fontSize: '11px' }}>
              Full URL: <span className="text-primary">{BASE_URL}{finalSlug || 'my-qr-link'}</span>
            </p>
          </div>

          {/* Expiry */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setHasExpiry(!hasExpiry)}
              className="flex items-center gap-2 text-foreground font-semibold"
              style={{ fontSize: '13px' }}
            >
              <div className={`w-9 h-5 rounded-full transition-all duration-200 relative ${hasExpiry ? 'bg-primary' : 'bg-muted'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${hasExpiry ? 'left-4' : 'left-0.5'}`} />
              </div>
              Set Expiry Date
            </button>
          </div>
          {hasExpiry && (
            <input
              type="datetime-local"
              value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl border border-border bg-muted/30 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              style={{ fontSize: '14px' }}
            />
          )}
        </div>

        <div className="px-6 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 btn-ghost py-2.5 rounded-2xl font-semibold" style={{ fontSize: '14px' }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || !qrContent.trim()}
            className="flex-1 btn-primary py-2.5 rounded-2xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: '14px' }}
          >
            Generate Link
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkAnalyticsDrawer({ link, onClose }: { link: SharedLink; onClose: () => void }) {
  const ctr = link.views > 0 ? ((link.clicks / link.views) * 100).toFixed(1) : '0.0';
  const uniqueRate = link.views > 0 ? ((link.uniqueVisitors / link.views) * 100).toFixed(0) : '0';
  const shareUrl = `${BASE_URL}${link.slug}`;
  const meta = TYPE_META[link.qrType] ?? { label: link.qrType, color: '#475569', bg: '#F8FAFC' };

  const stats = [
    { label: 'Total Views', value: link.views.toLocaleString(), icon: '👁', color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Unique Visitors', value: link.uniqueVisitors.toLocaleString(), icon: '👤', color: '#7C3AED', bg: '#F5F3FF' },
    { label: 'Total Clicks', value: link.clicks.toLocaleString(), icon: '🖱', color: '#16A34A', bg: '#F0FDF4' },
    { label: 'Click-Through Rate', value: `${ctr}%`, icon: '📊', color: '#D97706', bg: '#FFFBEB' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-card w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl border border-border shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-start justify-between sticky top-0 bg-card z-10">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 rounded-full font-semibold" style={{ fontSize: '11px', background: meta.bg, color: meta.color }}>{meta.label}</span>
              <span className={`px-2 py-0.5 rounded-full font-semibold ${link.active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`} style={{ fontSize: '11px' }}>
                {link.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <h3 className="font-bold text-foreground truncate" style={{ fontSize: '16px' }}>{link.title}</h3>
            <p className="text-muted-foreground truncate mt-0.5" style={{ fontSize: '12px' }}>{link.qrContent}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2 rounded-xl flex-shrink-0" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Shareable URL */}
          <div>
            <p className="text-muted-foreground font-semibold mb-2" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Shareable URL</p>
            <div className="flex items-center gap-2 p-3 rounded-2xl border border-border bg-muted/30">
              <span className="flex-1 text-primary font-medium truncate" style={{ fontSize: '13px' }}>{shareUrl}</span>
              <CopyButton text={shareUrl} />
            </div>
          </div>

          {/* Analytics Grid */}
          <div>
            <p className="text-muted-foreground font-semibold mb-3" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Analytics</p>
            <div className="grid grid-cols-2 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl border border-border p-4" style={{ background: s.bg }}>
                  <p className="text-2xl mb-1">{s.icon}</p>
                  <p className="font-extrabold" style={{ fontSize: '22px', color: s.color, letterSpacing: '-0.02em', lineHeight: 1 }}>{s.value}</p>
                  <p className="text-muted-foreground mt-1" style={{ fontSize: '11px', fontWeight: 600 }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Engagement bar */}
          <div className="rounded-2xl border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-foreground font-semibold" style={{ fontSize: '13px' }}>Unique Visitor Rate</span>
              <span className="font-bold text-primary" style={{ fontSize: '14px' }}>{uniqueRate}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full gradient-primary transition-all duration-700" style={{ width: `${uniqueRate}%` }} />
            </div>
            <p className="text-muted-foreground mt-2" style={{ fontSize: '11px' }}>{link.uniqueVisitors.toLocaleString()} unique out of {link.views.toLocaleString()} total views</p>
          </div>

          {/* Meta */}
          <div className="rounded-2xl border border-border divide-y divide-border overflow-hidden">
            {[
              { label: 'Created', value: new Date(link.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
              { label: 'Expires', value: link.expiresAt ? new Date(link.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never' },
              { label: 'Last Click', value: link.lastClickedAt ? new Date(link.lastClickedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'No clicks yet' },
              { label: 'Slug', value: `/${link.slug}` },
            ].map(row => (
              <div key={row.label} className="flex items-center justify-between px-4 py-3">
                <span className="text-muted-foreground" style={{ fontSize: '12px' }}>{row.label}</span>
                <span className="text-foreground font-medium" style={{ fontSize: '12px' }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ShareableLinksClient() {
  const [links, setLinks] = useState<SharedLink[]>(MOCK_LINKS);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [embedLink, setEmbedLink] = useState<SharedLink | null>(null);
  const [analyticsLink, setAnalyticsLink] = useState<SharedLink | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const totalViews = useMemo(() => links.reduce((s, l) => s + l.views, 0), [links]);
  const totalClicks = useMemo(() => links.reduce((s, l) => s + l.clicks, 0), [links]);
  const activeCount = useMemo(() => links.filter(l => l.active).length, [links]);
  const avgCtr = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : '0.0';

  const filtered = useMemo(() => links.filter(l => {
    const matchSearch = !search || l.title.toLowerCase().includes(search.toLowerCase()) || l.slug.includes(search.toLowerCase()) || l.qrContent.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || (filterStatus === 'active' ? l.active : !l.active);
    return matchSearch && matchStatus;
  }), [links, search, filterStatus]);

  const handleCopyUrl = useCallback((link: SharedLink) => {
    navigator.clipboard.writeText(`${BASE_URL}${link.slug}`).then(() => {
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  const handleToggleActive = useCallback((id: string) => {
    setLinks(prev => prev.map(l => l.id === id ? { ...l, active: !l.active } : l));
  }, []);

  const handleDelete = useCallback((id: string) => {
    setLinks(prev => prev.filter(l => l.id !== id));
  }, []);

  const handleCreate = useCallback((link: SharedLink) => {
    setLinks(prev => [link, ...prev]);
  }, []);

  return (
    <div className="min-h-screen">
      {/* ── Page Header ── */}
      <div className="px-6 lg:px-8 xl:px-10 pt-8 pb-6 border-b border-border bg-card">
        <div className="max-w-screen-2xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-lg gradient-primary flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2H10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h2" />
                  <rect x="3.5" y="1" width="5" height="2.5" rx="1" />
                  <path d="M4 7h4M4 9h2" />
                </svg>
              </div>
              <span className="badge badge-blue">Sharing</span>
            </div>
            <h1 className="font-extrabold text-foreground" style={{ fontSize: '26px', letterSpacing: '-0.02em' }}>Shareable Links</h1>
            <p className="text-muted-foreground mt-0.5" style={{ fontSize: '14px' }}>
              Generate unique trackable links for each QR code — with embeddable HTML snippets and per-link analytics.
            </p>
          </div>
          <button
            onClick={() => setShowGenerate(true)}
            className="btn-primary flex items-center gap-2 px-5 py-2.5 rounded-2xl font-semibold flex-shrink-0"
            style={{ fontSize: '14px' }}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
              <line x1="7.5" y1="2" x2="7.5" y2="13" /><line x1="2" y1="7.5" x2="13" y2="7.5" />
            </svg>
            New Shareable Link
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-8 xl:px-10 py-6">
        <div className="max-w-screen-2xl mx-auto space-y-6">

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Links"
              value={links.length}
              sub={`${activeCount} active`}
              accent="#EFF6FF"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#2563EB" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M7.5 10.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5l-1 1" /><path d="M10.5 7.5a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5l1-1" /></svg>}
            />
            <StatCard
              label="Total Views"
              value={totalViews.toLocaleString()}
              sub="Across all links"
              accent="#F5F3FF"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#7C3AED" strokeWidth="1.7" strokeLinecap="round"><circle cx="9" cy="9" r="3" /><path d="M1.5 9C3.5 4.5 7 2.5 9 2.5S14.5 4.5 16.5 9C14.5 13.5 11 15.5 9 15.5S3.5 13.5 1.5 9Z" /></svg>}
            />
            <StatCard
              label="Total Clicks"
              value={totalClicks.toLocaleString()}
              sub="QR link interactions"
              accent="#F0FDF4"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3l9 6-9 6V3Z" /></svg>}
            />
            <StatCard
              label="Avg. CTR"
              value={`${avgCtr}%`}
              sub="Click-through rate"
              accent="#FFFBEB"
              icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#D97706" strokeWidth="1.7" strokeLinecap="round"><path d="M2 13L6 8L10 10.5L16 4" /><circle cx="16" cy="4" r="1.5" fill="#D97706" stroke="none" /></svg>}
            />
          </div>

          {/* ── Filters ── */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <circle cx="6.5" cy="6.5" r="5" /><path d="M11 11l2.5 2.5" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by title, slug, or content…"
                className="w-full pl-10 pr-4 py-2.5 rounded-2xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                style={{ fontSize: '14px' }}
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'active', 'inactive'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className="px-4 py-2.5 rounded-2xl font-semibold transition-all duration-150 border"
                  style={{
                    fontSize: '13px',
                    background: filterStatus === s ? 'var(--primary)' : 'var(--card)',
                    color: filterStatus === s ? 'white' : 'var(--muted-foreground)',
                    borderColor: filterStatus === s ? 'var(--primary)' : 'var(--border)',
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* ── Links Table ── */}
          <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-bold text-foreground" style={{ fontSize: '15px' }}>
                All Links <span className="text-muted-foreground font-normal ml-1" style={{ fontSize: '13px' }}>({filtered.length})</span>
              </h2>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="w-14 h-14 rounded-3xl gradient-primary-soft flex items-center justify-center mb-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <p className="font-bold text-foreground" style={{ fontSize: '16px' }}>No links found</p>
                <p className="text-muted-foreground mt-1" style={{ fontSize: '13px' }}>Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map((link) => {
                  const meta = TYPE_META[link.qrType] ?? { label: link.qrType, color: '#475569', bg: '#F8FAFC' };
                  const shareUrl = `${BASE_URL}${link.slug}`;
                  const isCopied = copiedId === link.id;
                  const ctr = link.views > 0 ? ((link.clicks / link.views) * 100).toFixed(1) : '0.0';

                  return (
                    <div key={link.id} className="px-6 py-4 hover:bg-muted/20 transition-colors">
                      {/* Top row */}
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-bold text-foreground" style={{ fontSize: '14px' }}>{link.title}</span>
                            <span className="px-2 py-0.5 rounded-full font-semibold" style={{ fontSize: '10px', background: meta.bg, color: meta.color }}>{meta.label}</span>
                            <span className={`px-2 py-0.5 rounded-full font-semibold ${link.active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`} style={{ fontSize: '10px' }}>
                              {link.active ? '● Active' : '○ Inactive'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-primary font-medium truncate" style={{ fontSize: '12px', maxWidth: '280px' }}>{shareUrl}</span>
                            <button
                              onClick={() => handleCopyUrl(link)}
                              className="flex-shrink-0 p-1 rounded-lg hover:bg-muted/50 transition-colors"
                              title="Copy URL"
                            >
                              {isCopied ? (
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,7 5,10 11,3" /></svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="7.5" height="7.5" rx="1.2" /><path d="M1.5 9V2.5A1 1 0 0 1 2.5 1.5H9" /></svg>
                              )}
                            </button>
                          </div>
                          <p className="text-muted-foreground truncate" style={{ fontSize: '11px', maxWidth: '320px' }}>{link.qrContent}</p>
                        </div>

                        {/* Stats chips */}
                        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                          <div className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-muted/50">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round"><circle cx="6" cy="6" r="2" /><path d="M1 6C2.5 3 4 2 6 2S9.5 3 11 6C9.5 9 8 10 6 10S2.5 9 1 6Z" /></svg>
                            <span className="font-semibold text-foreground" style={{ fontSize: '12px' }}>{link.views.toLocaleString()}</span>
                            <span className="text-muted-foreground" style={{ fontSize: '11px' }}>views</span>
                          </div>
                          <div className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-muted/50">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2l7 4-7 4V2Z" /></svg>
                            <span className="font-semibold text-foreground" style={{ fontSize: '12px' }}>{link.clicks.toLocaleString()}</span>
                            <span className="text-muted-foreground" style={{ fontSize: '11px' }}>clicks</span>
                          </div>
                          <div className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-muted/50">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round"><path d="M1 9L4 6L7 7.5L11 3" /></svg>
                            <span className="font-semibold text-foreground" style={{ fontSize: '12px' }}>{ctr}%</span>
                            <span className="text-muted-foreground" style={{ fontSize: '11px' }}>CTR</span>
                          </div>
                        </div>
                      </div>

                      {/* Action row */}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <button
                          onClick={() => setAnalyticsLink(link)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card hover:bg-muted/50 transition-all"
                          style={{ fontSize: '12px', fontWeight: 600, color: 'var(--foreground)' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round"><path d="M1.5 10L4 7L7 8.5L11.5 3" /><circle cx="11.5" cy="3" r="1" fill="#2563EB" stroke="none" /></svg>
                          Analytics
                        </button>
                        <button
                          onClick={() => setEmbedLink(link)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card hover:bg-muted/50 transition-all"
                          style={{ fontSize: '12px', fontWeight: 600, color: 'var(--foreground)' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#7C3AED" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 4L2 6.5 4.5 9" /><path d="M8.5 4L11 6.5 8.5 9" /><path d="M7 3l-1.5 7" /></svg>
                          Embed
                        </button>
                        <button
                          onClick={() => handleToggleActive(link.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card hover:bg-muted/50 transition-all"
                          style={{ fontSize: '12px', fontWeight: 600, color: link.active ? '#D97706' : '#16A34A' }}
                        >
                          {link.active ? (
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#D97706" strokeWidth="1.6" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="5.5" /><path d="M4.5 6.5h4" /></svg>
                          ) : (
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#16A34A" strokeWidth="1.6" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="5.5" /><path d="M4.5 6.5h4M6.5 4.5v4" /></svg>
                          )}
                          {link.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(link.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-100 bg-red-50 hover:bg-red-100 transition-all ml-auto"
                          style={{ fontSize: '12px', fontWeight: 600, color: '#DC2626' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#DC2626" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="2,3.5 11,3.5" /><path d="M4.5 3.5V2.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1" />
                            <path d="M3 3.5l.7 7a1 1 0 0 0 1 .9h3.6a1 1 0 0 0 1-.9l.7-7" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── How It Works ── */}
          <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">
            <div className="px-6 py-5 border-b border-border">
              <h2 className="font-bold text-foreground" style={{ fontSize: '15px' }}>How Shareable Links Work</h2>
              <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Each link is a unique, trackable URL that wraps your QR code</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
              {[
                {
                  step: '01', title: 'Generate a Link', desc: 'Create a unique short URL for any QR code. Customize the slug and set an optional expiry date.',
                  icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07-7.07l-1.41-1.41" /><path d="M10 7a5 5 0 0 0-7.07 7.07l1.41 1.41" /><line x1="8" y1="12" x2="12" y2="8" /></svg>,
                  bg: '#EFF6FF',
                },
                {
                  step: '02', title: 'Share or Embed', desc: 'Copy the URL to share anywhere, or grab the HTML snippet to embed an iFrame, button, or badge on any webpage.',
                  icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#7C3AED" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M7 8L4 10l3 2" /><path d="M13 8l3 2-3 2" /><path d="M11 6l-2 8" /></svg>,
                  bg: '#F5F3FF',
                },
                {
                  step: '03', title: 'Track Analytics', desc: 'Monitor views, unique visitors, clicks, and CTR per link in real time. Identify your best-performing QR codes.',
                  icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#16A34A" strokeWidth="1.6" strokeLinecap="round"><path d="M3 15L7 10L11 12L17 5" /><circle cx="17" cy="5" r="1.5" fill="#16A34A" stroke="none" /></svg>,
                  bg: '#F0FDF4',
                },
              ].map(item => (
                <div key={item.step} className="px-6 py-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: item.bg }}>
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-muted-foreground font-bold mb-1" style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Step {item.step}</p>
                    <p className="font-bold text-foreground mb-1" style={{ fontSize: '14px' }}>{item.title}</p>
                    <p className="text-muted-foreground" style={{ fontSize: '12px', lineHeight: 1.6 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Modals ── */}
      {embedLink && <EmbedModal link={embedLink} onClose={() => setEmbedLink(null)} />}
      {analyticsLink && <LinkAnalyticsDrawer link={analyticsLink} onClose={() => setAnalyticsLink(null)} />}
      {showGenerate && <GenerateLinkModal onClose={() => setShowGenerate(false)} onCreate={handleCreate} />}
    </div>
  );
}
